import { ColumnsType } from "antd/lib/table"
import moment from "moment-timezone"
import GenericTable, { GenericTableParams } from "components/generic-table/generic-table"
import { TApiSavedReport, TApiSavedReportKind, TApiSavedReportsFilters } from "@timenotes/shared/src/services/saved-reports/saved-reports-types"
import { Modal, Tooltip, message } from "antd"
import { BarChartOutlined, TableOutlined } from "@ant-design/icons"
import { toTitleCase } from "utils/string-utils"
import { MoreOptionsButton } from "../ui/more-options-button"
import { useDeleteSavedReportMutation } from "@timenotes/shared/src/services/saved-reports/queries/use-delete-saved-report-mutation"
import { useHistory } from "react-router-dom"
import { useSavedReportsQuery } from "@timenotes/shared/src/services/saved-reports/queries/use-saved-reports-query"
import { getSavedReportPath } from "pages/reports/utils/get-saved-report-path"
import { handleGenericAsyncError } from "services/utils/functions/handle-generic-async-error"

interface Props {
  filters: TApiSavedReportsFilters
}

export function SavedReportsTable(props: Props) {
  const {
    filters
  } = props

  const history = useHistory()

  const savedReportsQuery = useSavedReportsQuery({
    filters: filters 
  })

  const deleteSavedReportMutation = useDeleteSavedReportMutation()

  const handleDelete = (savedReport: TApiSavedReport) => {
    Modal.confirm({
      title: `Are you sure you want to delete the saved report - "${savedReport.name}"`,
      content: savedReport.visibility == 'admin' ? 'Keep in mind it will delete the saved report for all the admins in the workspace.' : undefined,
      onOk: async () => handleGenericAsyncError(() => deleteSavedReportMutation.mutateAsync(savedReport.id)),
      okText: 'Delete',
    })

  }

  const COLUMNS: ColumnsType<TApiSavedReport> = [
    {
      title: "Name",
      width: '50%',
      render: (_, record) => (
        <div
          onClick={() => {
            history.push(getSavedReportPath(record))

          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
          }}
        >
          <SavedReportKindIcon kind={record.kind} />
          {record.name}
        </div>
      )
    },
    {
      title: () => (
        "Visibility"
      ),
      render: (_, record) => toTitleCase(record.visibility as string)
    },
    {
      key: "updatedAt",
      title: "Last Update Date",
      render: (_, record) => {
        const displayDate = moment(record.updatedAt).format("MMM D, YYYY")
        const tooltipContent = record.updaterUsersAccount 
          ? `Updated by ${record.updaterUsersAccount.firstName} ${record.updaterUsersAccount.lastName} on ${moment(record.updatedAt).format("MMM D, YYYY")}`
          : `Created by ${record.usersAccount.firstName} ${record.usersAccount.lastName} on ${moment(record.createdAt).format("MMM D, YYYY")}`
        
        return (
          <Tooltip title={tooltipContent}>
            <span>{displayDate}</span>
          </Tooltip>
        )
      }
    },
    {
      key: "createdBy",
      title: "Created By",
      render: (_, record) => {
        const displayName = `${record.usersAccount.firstName} ${record.usersAccount.lastName}`
        const tooltipContent = `Created by ${displayName} on ${moment(record.createdAt).format("MMM D, YYYY")}`
        
        return (
          <Tooltip title={tooltipContent}>
            <span>{displayName}</span>
          </Tooltip>
        )
      }
    },
    {
      key: "actions",
      title: "",
      render: (record) => (
        <MoreOptionsButton 
          options={[
            {
              title: 'Go to saved report',
              icon: 'report',
              onClick: () => {
                history.push(getSavedReportPath(record))
              },
            },
            {
              title: 'Delete saved report',
              icon: 'delete',
              onClick: () => {
                handleDelete(record)
              },
            }
          ]}
        />
      )
    }
  ]

  const tableParams: GenericTableParams = {
    pagination: null,
    sorting: null,
    selectedRowKeys: [],
    selectedAll: false
  }

  return (
    <GenericTable
      tableProps={{
        loading: savedReportsQuery.isLoading,
        columns: COLUMNS,
        dataSource: savedReportsQuery.data?.savedReports || []
      }}
      tableParams={tableParams}
      onChange={() => {}}
    />
  )
}

function SavedReportKindIcon(props: { kind: TApiSavedReportKind}) {
  const component = props.kind == 'report' ? <BarChartOutlined /> : <TableOutlined />
  const hint = `It is a ${toTitleCase(props.kind)}.`

  return (
    <Tooltip 
      title={hint}
    >
      {component}
    </Tooltip>
  )
}